'use client';

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { solicitudService } from '@/lib/api/solicitudService';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import ErrorMessage from '@/components/common/ErrorMessage';
import { useAuth } from '@/hooks/useAuth';

interface SeguimientoDiarioItem {
  id: number;
  areaResponsable: string;
  estatus: string;
  producto: string;
  promotor: string;
  empresa: string;
  cliente: string;
  montoSolicitado: number;
  fecha: string;
  fechaFormateada: string;
  diasTranscurridos: number;
  bgcolor: string;
  color: string;
  alerta: boolean;
}

interface GroupedSeguimientoRow {
  row: SeguimientoDiarioItem;
  showAreaResponsable: boolean;
  areaResponsableSpan: number;
  showEstatus: boolean;
  estatusSpan: number;
  showProducto: boolean;
  productoSpan: number;
  showPromotor: boolean;
  promotorSpan: number;
  showEmpresa: boolean;
  empresaSpan: number;
}

const SEGUIMIENTO_FILTER_FIELDS = [
  { value: 'areaResponsable', label: 'Área Responsable' },
  { value: 'estatus', label: 'Estatus' },
  { value: 'producto', label: 'Producto' },
  { value: 'promotor', label: 'Promotor' },
  { value: 'empresa', label: 'Empresa' },
  { value: 'cliente', label: 'Cliente' },
];

const removeAccents = (str: string) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const compareText = (a?: string, b?: string) => (a ?? '').localeCompare(b ?? '', 'es', { sensitivity: 'base' });

const SeguimientoDiarioManagement: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const [reporte, setReporte] = useState<SeguimientoDiarioItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterField, setFilterField] = useState('empresa');
  const [filterValue, setFilterValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const load = useCallback(async () => {
    if (authLoading) return;
    if (!user?.sub) {
      setError('Usuario no autenticado');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const res = await solicitudService.getSeguimientoDiario(user.sub);
      if (res.data) {
        setReporte(res.data);
      } else {
        setError(res.error || 'Error al cargar reporte de seguimiento diario');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar reporte de seguimiento diario');
      console.error('Error fetching seguimiento diario:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user?.sub, authLoading]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredReporte = useMemo(() => {
    let list = reporte;
    if (filterValue.trim() !== '') {
      const val = filterValue.trim().toLowerCase();
      const isWild = val === '*';
      const isEmpty = val === '""' || val === '';
      list = list.filter((r) => {
        const cell = String((r as any)[filterField] ?? '').toLowerCase();
        if (isWild) return cell !== '';
        if (isEmpty) return cell === '';
        return removeAccents(cell).includes(removeAccents(val));
      });
    }
    if (searchTerm.trim() !== '') {
      const term = removeAccents(searchTerm.toLowerCase());
      list = list.filter((r) =>
        removeAccents((r.areaResponsable ?? '').toLowerCase()).includes(term) ||
        removeAccents((r.estatus ?? '').toLowerCase()).includes(term) ||
        removeAccents((r.producto ?? '').toLowerCase()).includes(term) ||
        removeAccents((r.promotor ?? '').toLowerCase()).includes(term) ||
        removeAccents((r.empresa ?? '').toLowerCase()).includes(term) ||
        removeAccents((r.cliente ?? '').toLowerCase()).includes(term)
      );
    }
    return list;
  }, [reporte, filterField, filterValue, searchTerm]);

  const sortedReporte = useMemo(() => {
    const list = [...filteredReporte];
    list.sort((a, b) => {
      const byArea = compareText(a.areaResponsable, b.areaResponsable);
      if (byArea !== 0) return byArea;
      const byEstatus = compareText(a.estatus, b.estatus);
      if (byEstatus !== 0) return byEstatus;
      const byProducto = compareText(a.producto, b.producto);
      if (byProducto !== 0) return byProducto;
      const byPromotor = compareText(a.promotor, b.promotor);
      if (byPromotor !== 0) return byPromotor;
      const byEmpresa = compareText(a.empresa, b.empresa);
      if (byEmpresa !== 0) return byEmpresa;
      return compareText(a.cliente, b.cliente);
    });
    return list;
  }, [filteredReporte]);

  const groupedReporte = useMemo(() => {
    const groupedRows: GroupedSeguimientoRow[] = sortedReporte.map((row) => ({
      row,
      showAreaResponsable: false,
      areaResponsableSpan: 0,
      showEstatus: false,
      estatusSpan: 0,
      showProducto: false,
      productoSpan: 0,
      showPromotor: false,
      promotorSpan: 0,
      showEmpresa: false,
      empresaSpan: 0,
    }));

    let i = 0;
    while (i < sortedReporte.length) {
      const area = sortedReporte[i].areaResponsable;
      let areaEnd = i;
      while (areaEnd < sortedReporte.length && sortedReporte[areaEnd].areaResponsable === area) {
        areaEnd++;
      }
      groupedRows[i].showAreaResponsable = true;
      groupedRows[i].areaResponsableSpan = areaEnd - i;

      let j = i;
      while (j < areaEnd) {
        const estatus = sortedReporte[j].estatus;
        let estatusEnd = j;
        while (estatusEnd < areaEnd && sortedReporte[estatusEnd].estatus === estatus) {
          estatusEnd++;
        }
        groupedRows[j].showEstatus = true;
        groupedRows[j].estatusSpan = estatusEnd - j;

        let k = j;
        while (k < estatusEnd) {
          const producto = sortedReporte[k].producto;
          let productoEnd = k;
          while (productoEnd < estatusEnd && sortedReporte[productoEnd].producto === producto) {
            productoEnd++;
          }
          groupedRows[k].showProducto = true;
          groupedRows[k].productoSpan = productoEnd - k;

          let m = k;
          while (m < productoEnd) {
            const promotor = sortedReporte[m].promotor;
            let promotorEnd = m;
            while (promotorEnd < productoEnd && sortedReporte[promotorEnd].promotor === promotor) {
              promotorEnd++;
            }
            groupedRows[m].showPromotor = true;
            groupedRows[m].promotorSpan = promotorEnd - m;

            let n = m;
            while (n < promotorEnd) {
              const empresa = sortedReporte[n].empresa;
              let empresaEnd = n;
              while (empresaEnd < promotorEnd && sortedReporte[empresaEnd].empresa === empresa) {
                empresaEnd++;
              }
              groupedRows[n].showEmpresa = true;
              groupedRows[n].empresaSpan = empresaEnd - n;
              n = empresaEnd;
            }

            m = promotorEnd;
          }

          k = productoEnd;
        }

        j = estatusEnd;
      }

      i = areaEnd;
    }

    return groupedRows;
  }, [sortedReporte]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const handlePrint = useCallback(() => {
    const dateStr = new Date().toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });

    const printStyles = `
      body { font-family: Arial, sans-serif; font-size: 9px; margin: 0; padding: 0; }
      h1 { font-size: 14px; text-align: center; margin: 0 0 3px 0; }
      .subtitle { font-size: 9px; text-align: center; color: #555; margin: 0 0 8px 0; }
      table { border-collapse: collapse; width: 100%; table-layout: fixed; }
      th, td { border: 1px solid #000; padding: 2px 3px; word-wrap: break-word; overflow-wrap: break-word; }
      thead th { background: #d1d5db; font-weight: bold; font-size: 8px; text-transform: uppercase;
                 -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      th:nth-child(1), td:nth-child(1) { width: 10%; }
      th:nth-child(2), td:nth-child(2) { width: 13%; }
      th:nth-child(3), td:nth-child(3) { width: 10%; }
      th:nth-child(4), td:nth-child(4) { width: 12%; }
      th:nth-child(5), td:nth-child(5) { width: 14%; }
      th:nth-child(6), td:nth-child(6) { width: 18%; }
      th:nth-child(7), td:nth-child(7) { width: 12%; text-align: right; }
      th:nth-child(8), td:nth-child(8) { width: 7%; }
      th:nth-child(9), td:nth-child(9) { width: 4%; text-align: center; }
      tr { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      @page { size: letter landscape; margin: 0.3in; }
    `;

    const filas = groupedReporte.map((g) => {
      const r = g.row;
      const monto = new Intl.NumberFormat('es-MX', {
        style: 'currency', currency: 'MXN', minimumFractionDigits: 2
      }).format(r.montoSolicitado);
      const bg = r.bgcolor && r.bgcolor !== '#FFFFFF' ? `background-color:${r.bgcolor};` : '';
      const fg = r.color && r.color !== '#000000' ? `color:${r.color};` : '';
      const diasStyle = (bg || fg) ? ` style="text-align:center;font-weight:bold;${bg}${fg}"` : ' style="text-align:center;font-weight:bold;"';
      const areaCell = g.showAreaResponsable ? `<td rowspan="${g.areaResponsableSpan}"${r.alerta ? ' style="border-left:4px solid #ef4444;"' : ''}>${r.areaResponsable}</td>` : '';
      const estatusCell = g.showEstatus ? `<td rowspan="${g.estatusSpan}"><strong>${r.estatus}</strong></td>` : '';
      const productoCell = g.showProducto ? `<td rowspan="${g.productoSpan}">${r.producto}</td>` : '';
      const promotorCell = g.showPromotor ? `<td rowspan="${g.promotorSpan}">${r.promotor}</td>` : '';
      const empresaCell = g.showEmpresa ? `<td rowspan="${g.empresaSpan}">${r.empresa}</td>` : '';
      return `<tr>
        ${areaCell}
        ${estatusCell}
        ${productoCell}
        ${promotorCell}
        ${empresaCell}
        <td>${r.cliente}</td>
        <td style="text-align:right">${monto}</td>
        <td>${r.fechaFormateada}</td>
        <td${diasStyle}>${r.diasTranscurridos}</td>
      </tr>`;
    }).join('');

    const html = `<!DOCTYPE html><html><head><title>Seguimiento Diario</title>
      <meta charset="utf-8"><style>${printStyles}</style></head>
      <body>
        <h1>SEGUIMIENTO DIARIO</h1>
        <p class="subtitle">${dateStr} &mdash; ${filteredReporte.length} registros</p>
        <table>
          <thead><tr>
            <th>ÁREA</th><th>ESTATUS</th><th>PRODUCTO</th><th>PROMOTOR</th>
            <th>EMPRESA</th><th>CLIENTE</th><th>MONTO SOLICITADO</th>
            <th>FECHA</th><th>DÍAS</th>
          </tr></thead>
          <tbody>${filas}</tbody>
        </table>
      </body></html>`;

    const w = window.open('', 'PRINT');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
    w.close();
  }, [groupedReporte, filteredReporte.length]);

  if (authLoading || isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="no-print flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Seguimiento Diario</h1>
          <p className="text-sm text-gray-500 mt-1">
            Reporte de solicitudes en proceso con responsable y días laborales transcurridos.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={load}>
            Recargar
          </Button>
          <Button variant="secondary" onClick={handlePrint}>
            Imprimir
          </Button>
        </div>
      </div>

      {error && <div className="no-print"><ErrorMessage message={error} /></div>}

      {/* Filtros - mismo estilo que en Solicitudes */}
      <div className="no-print bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end mb-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Campo</label>
            <select
              value={filterField}
              onChange={(e) => { setFilterField(e.target.value); setFilterValue(''); }}
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {SEGUIMIENTO_FILTER_FIELDS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valor</label>
            <Input
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
              placeholder='Use * para campo lleno, "" para vacío'
            />
          </div>
          <div>
            <Button variant="secondary" onClick={() => setFilterValue('')}>Limpiar Filtro</Button>
          </div>
        </div>
        <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
          <div className="w-full md:w-2/3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Buscar por área, estatus, producto, promotor, empresa o cliente…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm"
              />
            </div>
          </div>
        </div>
        <div className="mt-3 text-xs text-gray-500">
          Mostrando <span className="font-medium text-gray-700">{filteredReporte.length}</span> de{' '}
          <span className="font-medium text-gray-700">{reporte.length}</span> registros
          {filterValue && (
            <span className="text-gray-400 ml-2">
              | Filtro: {SEGUIMIENTO_FILTER_FIELDS.find((f) => f.value === filterField)?.label} = {filterValue}
            </span>
          )}
        </div>
      </div>

      {/* Título solo visible al imprimir */}
      <div className="hidden print:block text-center mb-4">
        <h1 className="text-2xl font-bold">SEGUIMIENTO DIARIO</h1>
        <p className="text-sm text-gray-600">{new Date().toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' })}</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto print:shadow-none print:border-0 print:rounded-none">
        <table className="min-w-[1200px] w-full text-sm print:min-w-0">
          <thead className="bg-gray-50 text-gray-700 sticky top-0">
            <tr>
              <th className="text-left p-3 font-semibold">Área Responsable</th>
              <th className="text-left p-3 font-semibold">Estatus</th>
              <th className="text-left p-3 font-semibold">Producto</th>
              <th className="text-left p-3 font-semibold">Promotor</th>
              <th className="text-left p-3 font-semibold">Empresa</th>
              <th className="text-left p-3 font-semibold">Cliente</th>
              <th className="text-left p-3 font-semibold">Monto Solicitado</th>
              <th className="text-left p-3 font-semibold">Fecha</th>
              <th className="text-left p-3 font-semibold">Días Transcurridos</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {groupedReporte.map((g, idx) => {
              const r = g.row;
              return (
              <tr
                key={`${r.id}-${idx}`}
                className={`transition-colors ${r.alerta ? 'font-medium' : 'hover:bg-gray-50'}`}
              >
                {g.showAreaResponsable && (
                  <td rowSpan={g.areaResponsableSpan} className={`p-3 align-top ${r.alerta ? 'border-l-4 border-red-500' : ''}`}>{r.areaResponsable}</td>
                )}
                {g.showEstatus && (
                  <td rowSpan={g.estatusSpan} className="p-3 font-medium align-top">{r.estatus}</td>
                )}
                {g.showProducto && (
                  <td rowSpan={g.productoSpan} className="p-3 align-top">{r.producto}</td>
                )}
                {g.showPromotor && (
                  <td rowSpan={g.promotorSpan} className="p-3 align-top">{r.promotor}</td>
                )}
                {g.showEmpresa && (
                  <td rowSpan={g.empresaSpan} className="p-3 align-top">{r.empresa}</td>
                )}
                <td className="p-3">{r.cliente}</td>
                <td className="p-3 font-medium">
                  {formatCurrency(r.montoSolicitado)}
                </td>
                <td className="p-3">{r.fechaFormateada}</td>
                <td
                  className="p-3 font-bold text-lg"
                  style={{ backgroundColor: r.bgcolor, color: r.color }}
                >
                  {r.diasTranscurridos}
                </td>
              </tr>
              );
            })}
            {filteredReporte.length === 0 && (
              <tr>
                <td className="p-6 text-gray-500 text-center" colSpan={9}>
                  Sin registros en proceso.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <style jsx global>{`
        @media print {
          /* Ocultar TODO excepto el contenido del reporte */
          .no-print,
          nav,
          aside,
          header,
          footer,
          [data-sidebar],
          [class*="sidebar"],
          [class*="Sidebar"],
          [class*="navigation"],
          [class*="topbar"],
          [class*="header"] {
            display: none !important;
          }

          /* Reset completo del layout */
          html, body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            height: 100% !important;
            overflow: visible !important;
          }

          /* Eliminar márgenes y paddings de contenedores incluyendo sidebar */
          main,
          #__next,
          nav, aside, header, footer,
          [class*="main"],
          [class*="content"],
          [class*="container"],
          [class*="sidebar"],
          [class*="Sidebar"],
          [class*="layout"],
          .space-y-6,
          .space-y-6 > div {
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            position: static !important;
            transform: none !important;
            left: 0 !important;
            flex: none !important;
          }

          /* Contenedor de la tabla - ocupar todo el ancho */
          .bg-white.rounded-xl {
            margin: 0 !important;
            padding: 0 !important;
            border: none !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            width: 100% !important;
            overflow: visible !important;
          }

          /* Tabla - ajustar al 100% del ancho de página */
          table {
            border-collapse: collapse !important;
            width: 100% !important;
            min-width: 100% !important;
            max-width: 100% !important;
            font-size: 9px !important;
            table-layout: fixed !important;
          }

          th, td {
            border: 1px solid #000 !important;
            padding: 2px 4px !important;
            word-wrap: break-word !important;
            overflow: hidden !important;
          }

          /* Anchos de columnas proporcionales */
          th:nth-child(1), td:nth-child(1) { width: 10% !important; } /* Área */
          th:nth-child(2), td:nth-child(2) { width: 12% !important; } /* Estatus */
          th:nth-child(3), td:nth-child(3) { width: 10% !important; } /* Producto */
          th:nth-child(4), td:nth-child(4) { width: 12% !important; } /* Promotor */
          th:nth-child(5), td:nth-child(5) { width: 15% !important; } /* Empresa */
          th:nth-child(6), td:nth-child(6) { width: 18% !important; } /* Cliente */
          th:nth-child(7), td:nth-child(7) { width: 10% !important; } /* Monto */
          th:nth-child(8), td:nth-child(8) { width: 8% !important; }  /* Fecha */
          th:nth-child(9), td:nth-child(9) { width: 5% !important; }  /* Días */

          thead {
            background: #d1d5db !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          thead th {
            font-weight: bold !important;
            font-size: 8px !important;
            text-transform: uppercase !important;
          }

          /* Preservar colores de fondo en filas de alerta */
          tr, td {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          /* Título de impresión */
          .print\\:block {
            display: block !important;
            margin-bottom: 8px !important;
          }

          .print\\:block h1 {
            font-size: 16px !important;
            margin: 0 !important;
          }

          .print\\:block p {
            font-size: 10px !important;
            margin: 2px 0 0 0 !important;
          }

          /* Configuración de página - Carta horizontal */
          @page {
            margin: 0.3in;
            size: letter landscape;
          }
        }
      `}</style>
    </div>
  );
};

export default SeguimientoDiarioManagement;
